import React, { useState, useEffect } from 'react';
import { Offcanvas, Button, Row, Col, Badge } from 'react-bootstrap';
import axios from 'axios';
import { FaFilePdf, FaDownload, FaPrint, FaEye, FaEdit } from 'react-icons/fa';
import mammoth from 'mammoth';

const QuotationPreviewModal = ({
    showModal,
    handleClose,
    onEdit,
    onDownload,
    clientName,
    projectName,
    clientCurrency,
    brands,
    categories,
    selectedProducts = [],
    customIntegrationDescription = "",
    customIntegrationTotal = 0,
    formData,
    editableDeploymentDescriptions = '',
    isUploaded = false,
    uploadedFileUrl = null
}) => {
    console.log('🎯 QuotationPreviewModal rendered with showModal:', showModal);
    console.log('product', selectedProducts);
    console.log('formData', formData);
    console.log('🎯 QuotationPreviewModal rendered with deployment descriptions:', editableDeploymentDescriptions);

    const [products, setProducts] = useState([]);
    const [companyDetails, setCompanyDetails] = useState(null);
    const [fetchedCategories, setFetchedCategories] = useState(null);
    const [fetchedBrands, setFetchedBrands] = useState([]);

    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3333';
    const userInfo = JSON.parse(localStorage.getItem('user-info'));
    const origin = localStorage.getItem('user-origin');
    const token = userInfo?.token;

    // Use selectedProducts if available, otherwise fall back to formData.product_item_details
    const effectiveProducts = (Array.isArray(selectedProducts) && selectedProducts.length > 0)
        ? selectedProducts
        : (Array.isArray(formData?.product_item_details) && formData.product_item_details.length > 0)
            ? formData.product_item_details
            : [];

    const effectiveCategories = (categories?.allCategoriesList && categories.allCategoriesList.length > 0)
        ? categories
        : (fetchedCategories || { allCategoriesList: [], allSubcategories: [] });

    const effectiveBrands = (Array.isArray(brands) && brands.length > 0)
        ? brands
        : fetchedBrands;

    const effectiveClientCurrency =
        clientCurrency ||
        formData?.project?.client?.currency ||
        formData?.client?.currency ||
        formData?.currency ||
        formData?.import_currency ||
        effectiveProducts[0]?.product?.currency ||
        effectiveProducts[0]?.currency ||
        'AED';

    const effectiveClientName =
        clientName ||
        formData?.project?.client?.name ||
        formData?.client?.name ||
        formData?.customer_name ||
        'Customer Name';

    const effectiveProjectName =
        projectName ||
        formData?.project?.name ||
        formData?.project_name ||
        'Project Name';

    const effectiveCustomIntegrationDesc =
        customIntegrationDescription ||
        formData?.custom_integration_description ||
        formData?.custom_integration_html ||
        '';

    const effectiveCustomIntegrationTotal = parseFloat(
        customIntegrationTotal || formData?.custom_integration_total || 0
    );

    const fetchProducts = async () => {
        try {
            const response = await axios.get(`${backendUrl}/api/products`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                params: {
                    limit: 1000,
                },
                skipLoader: true,
            });
            let productsData = [];
            if (Array.isArray(response.data.Data)) {
                productsData = response.data.Data;
            } else if (response.data.Data && Array.isArray(response.data.Data.data)) {
                productsData = response.data.Data.data;
            } else if (response.data.data && Array.isArray(response.data.data)) {
                productsData = response.data.data;
            }
            setProducts(productsData);
        } catch (error) {
            console.error('Error fetching products:', error);
            setProducts([]);
        }
    };

    const fetchCompanyDetails = async () => {
        try {
            const response = await axios.get(`${backendUrl}/api/settings`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
            });
            setCompanyDetails(response.data.setting);
        } catch (error) {
            console.error('Error fetching company details:', error);
        }
    };

    useEffect(() => {
        if (!isUploaded) {
            fetchProducts();
        }
        fetchCompanyDetails();
    }, [isUploaded]);

    useEffect(() => {
        if (!categories?.allCategoriesList || categories.allCategoriesList.length === 0) {
            axios.get(`${backendUrl}/api/categories?type=product`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            }).then(res => {
                const allCats = res.data?.category || [];
                const allCategoriesList = allCats.filter(c => !c.parent_id);
                const allSubcategories = allCats.filter(c => c.parent_id);
                setFetchedCategories({ allCategoriesList, allSubcategories });
            }).catch(err => {
                console.error('Error fetching categories in preview:', err);
            });
        }

        if (!brands || brands.length === 0) {
            axios.get(`${backendUrl}/api/brands`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            }).then(res => {
                const brandList = res.data?.brands || res.data?.Data || res.data?.brand || [];
                setFetchedBrands(Array.isArray(brandList) ? brandList : []);
            }).catch(err => {
                console.error('Error fetching brands in preview:', err);
            });
        }
    }, [categories, brands, token, origin]);

    // Safe number formatting
    const safeToFixed = (value, decimals = 2) => {
        if (value === null || value === undefined) return '0.00';
        const num = parseFloat(value);
        return isNaN(num) ? '0.00' : num.toFixed(decimals);
    };

    const [docxHtml, setDocxHtml] = useState(null);
    const [isConvertingDocx, setIsConvertingDocx] = useState(false);

    const getFullDocumentUrl = () => {
        if (!uploadedFileUrl) return null;
        if (uploadedFileUrl.startsWith('http')) {
            return uploadedFileUrl;
        }
        const base = backendUrl.replace(/\/$/, '');
        const path = uploadedFileUrl.replace(/^\//, '').replace(/^storage\//, '');
        if (!path) return null;
        return `${base}/storage/${path}`;
    };

    const fullDocumentUrl = getFullDocumentUrl();
    const fileExtension = fullDocumentUrl?.split('.').pop()?.toLowerCase().split('?')[0];
    const isWord = ['doc', 'docx'].includes(fileExtension);
    const isExcel = ['xls', 'xlsx'].includes(fileExtension);
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension);
    const isPdf = fileExtension === 'pdf';

    useEffect(() => {
        if (isWord && fullDocumentUrl) {
            setIsConvertingDocx(true);
            axios.get(fullDocumentUrl, { responseType: 'arraybuffer' })
                .then(res => mammoth.convertToHtml({ arrayBuffer: res.data }))
                .then(result => {
                    setDocxHtml(result.value);
                    setIsConvertingDocx(false);
                })
                .catch(err => {
                    console.error('Error parsing docx:', err);
                    setDocxHtml('<div style="color: red; padding: 20px; text-align: center;">Failed to load local DOCX preview. Please download the file to view it.</div>');
                    setIsConvertingDocx(false);
                });
        } else {
            setDocxHtml(null);
        }
    }, [isWord, fullDocumentUrl]);

    const handleDocumentDownload = async () => {
        if (!fullDocumentUrl) return;
        const safeRefNo = formData?.ref_no?.replace(/[\/\\]/g, '-') || 'quotation';
        try {
            const response = await axios.get(fullDocumentUrl, { responseType: 'blob' });
            const fileExt = fullDocumentUrl?.split('.').pop()?.toLowerCase().split('?')[0] || 'pdf';
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = `quotation-${safeRefNo}.${fileExt}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading document:', error);
            window.open(fullDocumentUrl, '_blank');
        }
    };

    const renderUploadedDocumentPreview = () => {
        const absoluteDocUrl = fullDocumentUrl || '';
        const officeViewerUrl = absoluteDocUrl
            ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(absoluteDocUrl)}`
            : null;
        const googleViewerUrl = absoluteDocUrl
            ? `https://docs.google.com/gview?url=${encodeURIComponent(absoluteDocUrl)}&embedded=true`
            : null;

        return (
            <div style={{ padding: '20px', backgroundColor: 'white', minHeight: '100%', width: '100%', margin: '0 auto', fontSize: '12px' }}>
                <div className="mb-4">
                    <div className="p-0">
                        {fullDocumentUrl ? (
                            <div>
                                {isPdf ? (
                                    <div style={{ height: '1050px', width: '100%', overflow: 'hidden' }}>
                                        <iframe
                                            src={`${fullDocumentUrl}#toolbar=0&navpanes=0`}
                                            width="100%"
                                            height="100%"
                                            style={{ border: 'none', display: 'block' }}
                                            title={`Quotation Document - ${formData?.ref_no}`}
                                            frameBorder="0"
                                        />
                                    </div>
                                ) : (isWord || isExcel) ? (
                                    <div>
                                        <div style={{
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '8px',
                                            padding: '10px 14px',
                                            marginBottom: '10px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            border: '1px solid #dee2e6'
                                        }}>
                                            <span style={{ fontSize: '22px' }}>{isWord ? '📄' : '📊'}</span>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: 600, fontSize: '13px' }}>{isWord ? 'Word Document' : 'Excel Spreadsheet'}</div>
                                                <div style={{ color: '#6c757d', fontSize: '11px' }}>{isWord ? 'Local Document Viewer' : 'Previewing via Microsoft Office Online'}</div>
                                            </div>
                                            <a href={fullDocumentUrl} download target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary" style={{ fontSize: '12px' }}>⬇ Download</a>
                                        </div>
                                        <div style={{ minHeight: '600px', width: '100%', overflow: 'hidden' }}>
                                            {isWord ? (
                                                <div className="docx-viewer-container" style={{ backgroundColor: 'white', padding: '30px', border: '1px solid #e0e0e0', minHeight: '600px', overflowY: 'auto' }} dangerouslySetInnerHTML={{ __html: isConvertingDocx ? '<div style="text-align:center; padding: 20px;">Loading Document...</div>' : docxHtml }} />
                                            ) : (
                                                <iframe src={officeViewerUrl} width="100%" height="100%" style={{ border: 'none', display: 'block', minHeight: '1000px' }} title={`Quotation Document - ${formData?.ref_no}`} frameBorder="0" onError={(e) => { e.target.src = googleViewerUrl; }} />
                                            )}
                                        </div>
                                    </div>
                                ) : isImage ? (
                                    <div style={{ textAlign: 'center', width: '100%', overflow: 'auto', backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px' }}>
                                        <img src={fullDocumentUrl} alt={`Quotation Document - ${formData?.ref_no}`} style={{ maxWidth: '100%', maxHeight: '1050px', objectFit: 'contain' }} />
                                    </div>
                                ) : (
                                    <div className="text-center py-5">
                                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📎</div>
                                        <h5 className="mb-2">Preview Not Available</h5>
                                        <p className="text-muted mb-4" style={{ fontSize: '13px' }}>This file type (<strong>.{fileExtension}</strong>) cannot be previewed directly.<br />Please download the file to view it.</p>
                                        <a href={fullDocumentUrl} download target="_blank" rel="noreferrer" className="btn btn-primary">⬇ Download File</a>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-4">
                                <FaFilePdf size={40} className="text-danger mb-3" />
                                <h5>Document Not Available</h5>
                                <p className="text-muted">The uploaded quotation document could not be loaded.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const handleDocumentView = () => {
        if (uploadedFileUrl) {
            window.open(uploadedFileUrl, '_blank');
        }
    };

    const [editableDeploymentAmount, setEditableDeploymentAmount] = useState(0);

    const parseDeploymentDescriptions = (descriptions) => {
        if (!descriptions) return {};
        if (typeof descriptions === 'object') return descriptions;
        try {
            return JSON.parse(descriptions);
        } catch (e) {
            return {};
        }
    };

    const parsedDeploymentDescriptions = parseDeploymentDescriptions(
        editableDeploymentDescriptions || formData?.editable_deployment_descriptions
    );


    const getSelectedLicenseModel = () => {
        if (formData?.license_type_name) {
            return formData.license_type_name.toLowerCase();
        }
        const selectedLicenseId = formData?.license_type_id;
        const selectedLicenseObj = effectiveCategories?.allCategoriesList?.find(
            (c) => String(c.id) === String(selectedLicenseId)
        );
        if (selectedLicenseObj?.name) {
            return selectedLicenseObj.name.toLowerCase();
        }
        for (const item of effectiveProducts) {
            const prod = item?.product || item;
            if (prod?.revenue_type) return prod.revenue_type.toLowerCase();
            if (Array.isArray(prod?.pricings) && prod.pricings[0]?.license_model) {
                return prod.pricings[0].license_model.toLowerCase();
            }
        }
        if (formData?.subscription_period && Number(formData.subscription_period) > 0) {
            return 'subscription';
        }
        return 'subscription';
    };

    const getLicenseProducts = () => {
        const licenseProducts = effectiveProducts.filter(product => {
            try {
                const productData = product.product || product.pricings || product._product_raw || product;
                const isLicenseType =
                    product.license_type?.toLowerCase() === 'license' ||
                    product.licence_type?.toLowerCase() === 'license' ||
                    productData.categories?.some(cat =>
                        cat.licence_type?.toLowerCase() === 'license' || cat.license_type?.toLowerCase() === 'license'
                    ) ||
                    productData.license_type?.toLowerCase() === 'license' ||
                    productData.licence_type?.toLowerCase() === 'license' ||
                    product.revenue_type === 'perpetual' ||
                    productData.revenue_type === 'perpetual' ||
                    product.revenue_type === 'subscription' ||
                    productData.revenue_type === 'subscription' ||
                    (!productData.categories && !productData.license_type && !productData.licence_type);
                return isLicenseType;
            } catch (error) {
                console.error('Error checking product type:', error, product);
                return true;
            }
        });
        return licenseProducts;
    };

    const getDefaultServices = () => {
        if (!formData || formData.model_type !== 'new') {
            return [];
        }
        if (!Array.isArray(products)) {
            return [];
        }
        return products.filter(product => {
            const isServiceLicenseType = product.license_type?.toLowerCase() === 'service' || product.licence_type?.toLowerCase() === 'service';
            return isServiceLicenseType;
        });
    };

    const defaultServices = getDefaultServices();

    const usdToAedExchangeRate = 3.67;

    const convertPrice = (price, productCurrency = 'AED') => {
        if (price === undefined || price === null) return 0;
        const priceNumber = parseFloat(price);
        if (isNaN(priceNumber)) return 0;
        if (productCurrency === effectiveClientCurrency) {
            return priceNumber;
        }
        if (productCurrency === 'AED' && effectiveClientCurrency === 'USD') {
            return priceNumber / usdToAedExchangeRate;
        } else if (productCurrency === 'USD' && effectiveClientCurrency === 'AED') {
            return priceNumber * usdToAedExchangeRate;
        }
        return priceNumber;
    };

    const calculateDeploymentConfigurationAmount = () => {
        if (!formData || formData.model_type !== 'new') return 0;
        const selectedLicenseModel = getSelectedLicenseModel();
        const amount = defaultServices.reduce((total, service) => {
            if (service.license_type?.toLowerCase() !== 'service' && service.licence_type?.toLowerCase() !== 'service') return total;
            let price = 0;
            if (Array.isArray(service.pricings) && service.pricings.length > 0) {
                const matchingPricing = service.pricings.find(
                    (p) => p.license_model?.toLowerCase() === selectedLicenseModel
                ) || service.pricings[0];
                if (matchingPricing) {
                    price = Number(matchingPricing.price || matchingPricing.amount || 0);
                }
            }
            const serviceCurrency = service.currency || 'AED';
            const convertedAmount = convertPrice(price, serviceCurrency);
            return total + convertedAmount;
        }, 0);
        return amount;
    };


    const getProductDeploymentCost = (productItem) => {
        const selectedLicenseName = getSelectedLicenseModel();
        const directCost = productItem?.deployment_cost ?? productItem?.deployment_price;
        if (directCost !== undefined && directCost !== null && !isNaN(parseFloat(directCost)) && parseFloat(directCost) > 0) {
            const productCurrency = productItem?.product?.currency || productItem?.currency || 'AED';
            return convertPrice(parseFloat(directCost), productCurrency);
        }
        const pricings = (Array.isArray(productItem?.pricings) && productItem.pricings.length > 0)
            ? productItem.pricings
            : (productItem?.product?.pricings || []);
        if (!Array.isArray(pricings) || pricings.length === 0) {
            const rawCost = parseFloat(productItem?.product?.deployment_cost || productItem?.deployment_cost || 0);
            const productCurrency = productItem?.product?.currency || productItem?.currency || 'AED';
            return convertPrice(rawCost, productCurrency);
        }
        const matchedPricing =
            pricings.find((p) => p.license_model?.toLowerCase() === selectedLicenseName) ||
            pricings.find((p) => parseFloat(p.deployment_cost) > 0) ||
            pricings[0];
        const rawCost = parseFloat(matchedPricing?.deployment_cost || 0);
        const productCurrency = productItem?.product?.currency || productItem?.currency || 'AED';
        return convertPrice(rawCost, productCurrency);
    };

    const getProductDeploymentDescription = (productItem) => {
        const selectedLicenseName = getSelectedLicenseModel();
        if (productItem?.deployment_description) {
            return productItem.deployment_description;
        }
        const pricings = (Array.isArray(productItem?.pricings) && productItem.pricings.length > 0)
            ? productItem.pricings
            : (productItem?.product?.pricings || []);
        if (!Array.isArray(pricings) || pricings.length === 0) {
            return `${productItem.item_name || productItem.product?.product_name || 'Product'} Setup`;
        }
        const matchedPricing = selectedLicenseName
            ? pricings.find((p) => p.license_model?.toLowerCase?.() === selectedLicenseName)
            : pricings[0];
        return (
            matchedPricing?.deployment_description ||
            `${productItem.item_name || productItem.product?.product_name || 'Product'} Deployment & Configuration`
        );
    };

    const calculateOneTimeTotal = () => {
        return effectiveProducts.reduce((total, product) => {
            const deploymentCost = getProductDeploymentCost(product);
            return total + deploymentCost;
        }, 0);
    };

    const getSavedDeploymentAmount = () => {
        // Priority 1: Check service_amount.
        // In the database and QuotationForm, service_amount explicitly stores the base
        // "One Time deployment, Configuration & Testing" amount (e.g. 3000.00).
        if (formData?.service_amount !== undefined && formData?.service_amount !== null && formData?.service_amount !== '') {
            const num = parseFloat(formData.service_amount);
            if (!isNaN(num) && num > 0) {
                return num;
            }
        }

        // Priority 2: Check calculateDeploymentConfigurationAmount() from defaultServices
        // If model_type is "new" and defaultServices are present, this calculates the base service price (3000.00).
        const calcAmount = calculateDeploymentConfigurationAmount();
        if (calcAmount > 0) {
            return calcAmount;
        }

        // Priority 3: Check editable_deployment_amounts if stored as a number or numeric string
        if (formData?.editable_deployment_amounts !== undefined && formData?.editable_deployment_amounts !== null && formData?.editable_deployment_amounts !== '') {
            let val = formData.editable_deployment_amounts;
            if (typeof val === 'string') {
                try {
                    const parsed = JSON.parse(val);
                    if (typeof parsed === 'number') val = parsed;
                    else if (typeof parsed === 'string') val = parseFloat(parsed);
                } catch (e) {
                    val = parseFloat(val);
                }
            }
            if (typeof val === 'number' && !isNaN(val) && val > 0) {
                const individualDeploy = calculateOneTimeTotal();
                // If val equals the total deplyment_amount, it was mistakenly saved as total one-time cost, so subtract individual product deployments
                if (individualDeploy > 0 && Math.abs(val - parseFloat(formData?.deplyment_amount || 0)) < 0.01) {
                    return Math.max(0, val - individualDeploy - parseFloat(effectiveCustomIntegrationTotal || 0));
                }
                return val;
            }
        }

        // Priority 4: Fallback to deplyment_amount / deployment_amount.
        // Note: In QuotationForm, deplyment_amount stores finalOneTimeCost (the TOTAL one-time cost, e.g. 8875).
        // Subtract individual product deployment costs to avoid double-counting!
        const totalOneTime = formData?.deplyment_amount ?? formData?.deployment_amount;
        if (totalOneTime !== undefined && totalOneTime !== null && totalOneTime !== '') {
            const totalNum = parseFloat(totalOneTime);
            if (!isNaN(totalNum) && totalNum > 0) {
                const individualDeploy = calculateOneTimeTotal();
                const customInt = parseFloat(effectiveCustomIntegrationTotal || 0);
                const remaining = totalNum - individualDeploy - customInt;
                return Math.max(0, remaining);
            }
        }

        return 0;
    };

    useEffect(() => {
        const savedAmount = getSavedDeploymentAmount();
        if (savedAmount !== null && !isNaN(savedAmount)) {
            setEditableDeploymentAmount(savedAmount);
        } else if (formData?.model_type === 'new' && defaultServices.length > 0) {
            setEditableDeploymentAmount(prev => {
                if (prev === 0 || prev === '' || isNaN(prev)) {
                    return calculateDeploymentConfigurationAmount();
                }
                return prev;
            });
        }
    }, [defaultServices, formData?.model_type, formData?.editable_deployment_amounts, formData?.deployment_amount, formData?.deplyment_amount, formData?.service_amount, effectiveProducts]);


    const getAllDeploymentItems = () => {
        const items = [];
        effectiveProducts.forEach(product => {
            const deploymentCost = getProductDeploymentCost(product);
            const pId = product.product_id || product.id;
            const customDesc =
                parsedDeploymentDescriptions[pId] ||
                parsedDeploymentDescriptions[String(pId)] ||
                product.deployment_description;
            if (deploymentCost > 0 || customDesc) {
                const description =
                    customDesc ||
                    getProductDeploymentDescription(product) ||
                    `${product.item_name || product.product?.product_name || 'Product'} Deployment & Configuration`;
                items.push(description);
            }
        });
        if (effectiveCustomIntegrationDesc) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = effectiveCustomIntegrationDesc;
            const listItems = tempDiv.querySelectorAll('li, p, div');
            if (listItems.length > 0) {
                listItems.forEach(item => {
                    const text = item.textContent?.trim();
                    if (text && text.length > 0) {
                        items.push(text);
                    }
                });
            } else {
                const lines = effectiveCustomIntegrationDesc.split(/<br\s*\/?>|<\/p>|<\/div>|\n|\•|\-/);
                lines.forEach(line => {
                    const text = line.replace(/<[^>]*>/g, '').trim();
                    if (text && text.length > 0) {
                        items.push(text);
                    }
                });
            }
        }
        return items;
    };

    const calculateLicenseDiscount = (totalLicenseCost) => {
        const { license_discount_type = 'amount', license_discount_amount } = formData || {};
        if (!license_discount_amount) return 0;
        const discountType = license_discount_type || 'amount';
        return discountType === 'amount'
            ? parseFloat(license_discount_amount)
            : (license_discount_amount / 100) * totalLicenseCost;
    };

    const calculateServiceDiscount = (totalOneTimeCost) => {
        const { service_discount_type = 'amount', service_discount_amount } = formData || {};
        if (!service_discount_amount) return 0;
        const discountType = service_discount_type || 'amount';
        return discountType === 'amount'
            ? parseFloat(service_discount_amount)
            : (service_discount_amount / 100) * totalOneTimeCost;
    };

    const calculateOverallDiscount = (subtotal) => {
        const { discount_type = 'amount', discount_amount } = formData || {};
        if (!discount_amount) return 0;
        const discountType = discount_type || 'amount';
        return discountType === 'amount'
            ? parseFloat(discount_amount)
            : (discount_amount / 100) * subtotal;
    };

    const calculateTotals = () => {
        const licenseProducts = getLicenseProducts();
        const licenseTotal = licenseProducts.reduce((total, product) =>
            total + parseFloat(product.subtotal ?? (Number(product.quantity || 1) * Number(product.price || 0)) ?? 0), 0
        );
        const oneTimeTotal = calculateOneTimeTotal();
        const convertedIndividualDeploymentAmount = oneTimeTotal;
        const customIntegrationTotalValue = parseFloat(effectiveCustomIntegrationTotal || 0);
        const subscriptionPeriod = parseInt(formData?.subscription_period || 12);
        const selectedLicenseName = getSelectedLicenseModel();

        let totalLicenseCost = licenseTotal;
        if (selectedLicenseName === 'subscription') {
            totalLicenseCost = licenseTotal * subscriptionPeriod;
        }

        const effectiveDeploymentCost = editableDeploymentAmount > 0
            ? editableDeploymentAmount
            : (getSavedDeploymentAmount() || 0);

        const totalOneTimeCost = effectiveDeploymentCost + convertedIndividualDeploymentAmount + customIntegrationTotalValue;
        const licenseDiscountValue = calculateLicenseDiscount(totalLicenseCost);
        const serviceDiscountValue = calculateServiceDiscount(totalOneTimeCost);
        const licenseAfterDiscount = totalLicenseCost - licenseDiscountValue;
        const serviceAfterDiscount = totalOneTimeCost - serviceDiscountValue;
        const subtotalAfterIndividualDiscounts = licenseAfterDiscount + serviceAfterDiscount;
        const overallDiscountValue = calculateOverallDiscount(subtotalAfterIndividualDiscounts);
        const grandTotal = subtotalAfterIndividualDiscounts - overallDiscountValue;

        return {
            licenseProducts,
            licenseTotal,
            oneTimeTotal,
            editableDeploymentAmount: effectiveDeploymentCost,
            convertedIndividualDeploymentAmount,
            customIntegrationTotal: customIntegrationTotalValue,
            subscriptionPeriod,
            selectedLicenseName,
            totalLicenseCost,
            totalOneTimeCost,
            licenseDiscountValue,
            serviceDiscountValue,
            licenseAfterDiscount,
            serviceAfterDiscount,
            subtotalAfterIndividualDiscounts,
            overallDiscountValue,
            grandTotal
        };
    };

    const totals = calculateTotals();
    const allDeploymentItems = getAllDeploymentItems();

    const rawBrandId = Array.isArray(formData?.brand_id)
        ? formData.brand_id[0]
        : formData?.brand_id;
    const selectedBrand =
        formData?.brand?.name ||
        (Array.isArray(effectiveBrands) && effectiveBrands.find(b => String(b.id) === String(rawBrandId))?.name) ||
        effectiveProducts.find(p => p.product?.brand?.name)?.product?.brand?.name ||
        products.find(p => String(p.id) === String(rawBrandId) || String(p.brand_id) === String(rawBrandId))?.brand?.name ||
        'View360CX';

    const licenseDiscountType = formData?.license_discount_type || 'amount';
    const serviceDiscountType = formData?.service_discount_type || 'amount';
    const overallDiscountType = formData?.discount_type || 'amount';

    const renderSystemGeneratedPreview = () => {
        return (
            <div style={{
                padding: '20px',
                backgroundColor: 'white',
                minHeight: '100%',
                maxWidth: '210mm',
                margin: '0 auto',
                fontSize: '12px'
            }}>
                {/* NEW HEADER - Matching HTML Template */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    position: 'relative',
                    marginBottom: '10px'
                }}>
                    {/* Left Section - Company Details */}
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{
                            fontSize: '18px',
                            fontWeight: 'bold',
                            marginBottom: '6px'
                        }}>
                            {companyDetails?.company_name || 'SYSTECH TECHNOLOGIES'}
                        </div>
                        <div style={{
                            fontSize: '12px',
                            lineHeight: '1.5'
                        }}>
                            {companyDetails?.company_address ? (
                                <div dangerouslySetInnerHTML={{
                                    __html: companyDetails.company_address
                                }} />
                            ) : (
                                <>
                                    Dirt All Nokia Building, Office 307<br />
                                    P.O Box 91507
                                </>
                            )}
                            <br />
                            Phone: {companyDetails?.phone || 'N/A'}<br />
                            Email: {companyDetails?.email || 'N/A'}
                        </div>
                    </div>

                    {/* Right Section - Logo and Quotation Title */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        position: 'absolute',
                        right: '0',
                        top: '0'
                    }}>
                        {/* Logo */}
                        <div style={{
                            width: '180px',
                            height: '80px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            {companyDetails?.logo ? (
                                <img
                                    src={`${backendUrl}/storage/${companyDetails.logo}`}
                                    alt="Company Logo"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        objectFit: 'contain'
                                    }}
                                />
                            ) : (
                                <span style={{ color: '#666', fontSize: '11px' }}>Company Logo</span>
                            )}
                        </div>
                        <div style={{
                            fontSize: '24px',
                            fontWeight: 'bold',
                            color: 'black',
                        }}>
                            QUOTATION
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <div style={{
                    borderTop: '1px solid #ddd',
                    margin: '10px 0 20px 0'
                }}></div>

                {/* Quotation Details */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '20px',
                    width: '100%'
                }}>
                    {/* Left Column - Project Info */}
                    <div style={{
                        float: 'left',
                        width: '48%',
                        boxSizing: 'border-box'
                    }}>
                        <div style={{ marginBottom: '8px' }}>
                            <span style={{
                                display: 'inline-block',
                                width: '100px',
                                fontWeight: 'bold',
                                verticalAlign: 'top',
                                whiteSpace: 'nowrap'
                            }}>Project</span>
                            <span style={{
                                display: 'inline-block',
                                marginLeft: '8px',
                                wordBreak: 'break-word',
                                whiteSpace: 'normal',
                                lineHeight: '1.5',
                                maxWidth: 'calc(100% - 180px)',
                                verticalAlign: 'top'
                            }}>{effectiveProjectName}</span>
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                            <span style={{
                                display: 'inline-block',
                                width: '100px',
                                fontWeight: 'bold',
                                verticalAlign: 'top',
                                whiteSpace: 'nowrap'
                            }}>Document No</span>
                            <span style={{
                                display: 'inline-block',
                                marginLeft: '8px',
                                wordBreak: 'break-word',
                                whiteSpace: 'normal',
                                lineHeight: '1.5',
                                maxWidth: 'calc(100% - 180px)',
                                verticalAlign: 'top'
                            }}>{formData?.ref_no || 'Q25-AE/01/2049'}</span>
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                            <span style={{
                                display: 'inline-block',
                                width: '100px',
                                fontWeight: 'bold',
                                verticalAlign: 'top',
                                whiteSpace: 'nowrap'
                            }}>Date</span>
                            <span style={{
                                display: 'inline-block',
                                marginLeft: '8px',
                                wordBreak: 'break-word',
                                whiteSpace: 'normal',
                                lineHeight: '1.5',
                                maxWidth: 'calc(100% - 180px)',
                                verticalAlign: 'top'
                            }}>{formData?.date || '01 Jul 2025'}</span>
                        </div>
                    </div>

                    {/* Right Column - Customer Info */}
                    <div style={{
                        float: 'right',
                        width: '48%',
                        boxSizing: 'border-box'
                    }}>
                        <div style={{ marginBottom: '8px' }}>
                            <span style={{
                                display: 'inline-block',
                                width: '100px',
                                fontWeight: 'bold',
                                verticalAlign: 'top',
                                whiteSpace: 'nowrap'
                            }}>Customer:</span>
                            <span style={{
                                display: 'inline-block',
                                marginLeft: '8px',
                                wordBreak: 'break-word',
                                whiteSpace: 'normal',
                                lineHeight: '1.5',
                                maxWidth: 'calc(100% - 180px)',
                                verticalAlign: 'top'
                            }}>{effectiveClientName}</span>
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                            <span style={{
                                display: 'inline-block',
                                width: '100px',
                                fontWeight: 'bold',
                                verticalAlign: 'top',
                                whiteSpace: 'nowrap'
                            }}>Address:</span>
                            <span style={{
                                display: 'inline-block',
                                marginLeft: '8px',
                                wordBreak: 'break-word',
                                whiteSpace: 'normal',
                                lineHeight: '1.5',
                                maxWidth: 'calc(100% - 180px)',
                                verticalAlign: 'top'
                            }}>
                                {effectiveClientName ? `${effectiveClientName} Address` : 'Customer Address'}
                            </span>
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                            <span style={{
                                display: 'inline-block',
                                width: '100px',
                                fontWeight: 'bold',
                                verticalAlign: 'top',
                                whiteSpace: 'nowrap'
                            }}>Email:</span>
                            <span style={{
                                display: 'inline-block',
                                marginLeft: '8px',
                                wordBreak: 'break-word',
                                whiteSpace: 'normal',
                                lineHeight: '1.5',
                                maxWidth: 'calc(100% - 180px)',
                                verticalAlign: 'top'
                            }}>
                                {effectiveClientName ? `${effectiveClientName.toLowerCase().replace(/\s+/g, '')}@example.com` : 'customer@example.com'}
                            </span>
                        </div>
                        <div style={{ marginBottom: '8px' }}>
                            <span style={{
                                display: 'inline-block',
                                width: '100px',
                                fontWeight: 'bold',
                                verticalAlign: 'top',
                                whiteSpace: 'nowrap'
                            }}>Phone:</span>
                            <span style={{
                                display: 'inline-block',
                                marginLeft: '8px',
                                wordBreak: 'break-word',
                                whiteSpace: 'normal',
                                lineHeight: '1.5',
                                maxWidth: 'calc(100% - 180px)',
                                verticalAlign: 'top'
                            }}>
                                {effectiveClientName ? '+971-XXXXXXX' : '+971-66838999'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* ============================= LICENSE SECTION ============================= */}
                {totals.licenseProducts.length > 0 && (
                    <div className="mb-4 border rounded">
                        <div className="py-2 px-3 text-white fw-semibold" style={{ backgroundColor: "#1e4b7a", fontSize: "14px" }}>
                            {selectedBrand} - Licenses
                        </div>

                        <div className="table-responsive">
                            <table className="table table-bordered mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th style={{ width: "20%", fontSize: "12px", fontWeight: "bold" }}>License</th>
                                        <th style={{ width: "40%", fontSize: "12px", fontWeight: "bold" }}>Description</th>
                                        <th style={{ width: "10%", fontSize: "12px", fontWeight: "bold" }} className="text-center">Qty</th>
                                        <th style={{ width: "10%", fontSize: "12px", fontWeight: "bold" }} className="text-center">Rate</th>
                                        <th style={{ width: "10%", fontSize: "12px", fontWeight: "bold" }} className="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {totals.licenseProducts.map((product, index) => (
                                        <tr key={index}>
                                            <td style={{ fontSize: "12px" }}>
                                                {product.item_name || product.product?.product_name || product.product_name || 'Product'}
                                            </td>
                                            <td style={{ fontSize: "12px" }} className="text-start">{product.item_description || product.product?.description || product.description || '-'}</td>
                                            <td style={{ fontSize: "12px" }} className="text-center">{product.quantity || 1}</td>
                                            <td style={{ fontSize: "12px" }} className="text-center">
                                                {safeToFixed(product.price ?? product.product?.selling_price)}
                                            </td>
                                            <td style={{ fontSize: "12px" }} className="text-end">
                                                {safeToFixed(product.subtotal ?? (Number(product.quantity || 1) * Number(product.price || 0)))}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="4" className="text-end fw-medium" style={{ fontSize: "13px" }}>
                                            Total License Cost {totals.selectedLicenseName === 'subscription' ? 'per month' : ''}
                                        </td>
                                        <td className="text-end fw-medium" style={{ fontSize: "13px" }}>
                                            {safeToFixed(totals.licenseTotal)}
                                        </td>
                                    </tr>
                                    {totals.selectedLicenseName === 'subscription' && (
                                        <tr>
                                            <td colSpan="4" className="text-end fw-medium" style={{ fontSize: "13px" }}>
                                                Total License Cost per {totals.subscriptionPeriod} months
                                            </td>
                                            <td className="text-end fw-medium" style={{ fontSize: "13px" }}>
                                                {safeToFixed(totals.totalLicenseCost)}
                                            </td>
                                        </tr>
                                    )}
                                    {totals.licenseDiscountValue > 0 && (
                                        <tr>
                                            <td colSpan="4" className="text-end fw-medium text-dark" style={{ fontSize: "13px" }}>
                                                License Discount ({licenseDiscountType === 'amount' ? effectiveClientCurrency : '%'})
                                            </td>
                                            <td className="text-end fw-medium text-dark" style={{ fontSize: "13px" }}>
                                                -{safeToFixed(totals.licenseDiscountValue)}
                                            </td>
                                        </tr>
                                    )}

                                    {totals.licenseDiscountValue > 0 && (
                                        <tr>
                                            <td colSpan="4" className="text-end fw-bold text-dark" style={{ fontSize: "13px" }}>
                                                License After Discount
                                            </td>
                                            <td className="text-end fw-bold text-dark" style={{ fontSize: "13px" }}>
                                                {safeToFixed(totals.licenseAfterDiscount)}
                                            </td>
                                        </tr>
                                    )}
                                </tfoot>
                            </table>
                        </div>
                    </div>
                )}

                {/* ============================= ONE-TIME & INTEGRATION SECTION ============================= */}
                {(totals.editableDeploymentAmount > 0 || (totals.convertedIndividualDeploymentAmount + totals.customIntegrationTotal) > 0) && (
                    <div className="mb-4 border rounded">
                        <div className="py-2 px-3 text-white fw-semibold" style={{ backgroundColor: "#1e4b7a", fontSize: "14px" }}>
                            {selectedBrand} - Onboarding & Custom Integration
                        </div>

                        <div className="table-responsive">
                            <table className="table table-bordered mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th style={{ width: "65%", fontSize: "12px", fontWeight: "bold" }}>Description</th>
                                        <th style={{ width: "5%", fontSize: "12px", fontWeight: "bold" }}>QTY</th>
                                        <th style={{ width: "15%", fontSize: "12px", fontWeight: "bold" }} className="text-center">Rate</th>
                                        <th style={{ width: "15%", fontSize: "12px", fontWeight: "bold" }} className="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* ✅ One Time deployment, Configuration & Testing - Separate Row */}
                                    {totals.editableDeploymentAmount > 0 && (
                                        <tr>
                                            <td style={{ fontSize: "13px" }} className='text-start'>
                                                One Time deployment, Configuration & Testing
                                            </td>
                                            <td className="text-center" style={{ fontSize: "12px" }}>1</td>
                                            <td className="text-center" style={{ fontSize: "12px" }}>
                                                {safeToFixed(totals.editableDeploymentAmount)}
                                            </td>
                                            <td className="text-end" style={{ fontSize: "12px" }}>
                                                {safeToFixed(totals.editableDeploymentAmount)}
                                            </td>
                                        </tr>
                                    )}

                                    {/* ✅ Custom Integration with Ordered List */}
                                    {(totals.convertedIndividualDeploymentAmount + totals.customIntegrationTotal) > 0 && (
                                        <tr>
                                            <td className="text-start" style={{ fontSize: "13px" }}>
                                                <div className="fw-medium">Custom Integration</div>
                                                {allDeploymentItems.length > 0 && (
                                                    <div className="mt-2">
                                                        <ol className="mb-0" style={{ fontSize: "12px", paddingLeft: "20px" }}>
                                                            {allDeploymentItems.map((item, index) => (
                                                                <li key={index} className="text-muted">
                                                                    {item}
                                                                </li>
                                                            ))}
                                                        </ol>
                                                    </div>
                                                )}
                                            </td>
                                            <td className='text-center' style={{ fontSize: "12px" }}>1</td>
                                            <td className="text-center" style={{ fontSize: "12px" }}>
                                                {safeToFixed(totals.convertedIndividualDeploymentAmount + totals.customIntegrationTotal)}
                                            </td>
                                            <td className="text-end" style={{ fontSize: "12px" }}>
                                                {safeToFixed(totals.convertedIndividualDeploymentAmount + totals.customIntegrationTotal)}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="3" className="text-end fw-medium" style={{ fontSize: "13px" }}>
                                            Total One-time Cost ({effectiveClientCurrency})
                                        </td>
                                        <td className="text-end fw-medium" style={{ fontSize: "13px" }}>
                                            {safeToFixed(totals.totalOneTimeCost)}
                                        </td>
                                    </tr>
                                    {totals.serviceDiscountValue > 0 && (
                                        <tr>
                                            <td colSpan="3" className="text-end fw-medium text-dark" style={{ fontSize: "13px" }}>
                                                Service Discount ({serviceDiscountType === 'amount' ? effectiveClientCurrency : '%'})
                                            </td>
                                            <td className="text-end fw-medium text-dark" style={{ fontSize: "13px" }}>
                                                -{safeToFixed(totals.serviceDiscountValue)}
                                            </td>
                                        </tr>
                                    )}

                                    {/* ✅ SERVICE AFTER DISCOUNT */}
                                    {totals.serviceDiscountValue > 0 && (
                                        <tr>
                                            <td colSpan="3" className="text-end fw-bold text-dark" style={{ fontSize: "13px" }}>
                                                Services After Discount
                                            </td>
                                            <td className="text-end fw-bold text-dark" style={{ fontSize: "13px" }}>
                                                {safeToFixed(totals.serviceAfterDiscount)}
                                            </td>
                                        </tr>
                                    )}
                                </tfoot>
                            </table>
                        </div>
                    </div>
                )}
                <div className="mb-4 border rounded">
                    <div className="py-2 px-3 text-dark fw-semibold" style={{ backgroundColor: "#ffffff", fontSize: "14px" }}>
                        Final Summary
                    </div>
                    <div className="p-3">
                        <div className="row">
                            <div className="col-md-12">
                                {/* Subtotal after individual discounts */}
                                <div className="d-flex justify-content-between mb-2" style={{ fontSize: "13px" }}>
                                    <span className="fw-medium">Subtotal:</span>
                                    <span className="fw-medium">{safeToFixed(totals.subtotalAfterIndividualDiscounts)} {effectiveClientCurrency}</span>
                                </div>

                                {/* Overall Discount */}
                                {totals.overallDiscountValue > 0 && (
                                    <div className="d-flex justify-content-between mb-2 text-dark" style={{ fontSize: "13px" }}>
                                        <span className="fw-medium">Overall Discount ({overallDiscountType === 'amount' ? effectiveClientCurrency : '%'}):</span>
                                        <span className="fw-medium">-{safeToFixed(totals.overallDiscountValue)} {effectiveClientCurrency}</span>
                                    </div>
                                )}

                                {/* Grand Total */}
                                <div className="d-flex justify-content-between fw-bold pt-2 border-top" style={{ fontSize: "14px" }}>
                                    <span>Grand Total:</span>
                                    <span className="text-dark">{safeToFixed(totals.grandTotal)} {effectiveClientCurrency}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Terms & Conditions */}
                {formData?.notes && (
                    <div className="mt-4 pt-3 border-top">
                        <h6 className="fw-bold border-bottom pb-2">Terms & Conditions</h6>
                        <div
                            style={{ fontSize: "12px" }}
                            dangerouslySetInnerHTML={{ __html: formData.notes }}
                        />
                    </div>
                )}
            </div>
        );
    };


    return (
        <Offcanvas show={showModal} onHide={handleClose} placement="end" style={{ width: '1050px' }} className="quotation-preview-offcanvas">
            <Offcanvas.Header closeButton className="border-bottom bg-light position-relative">
                <Offcanvas.Title className="fw-semibold">
                    <i className="fa fa-file-pdf text-danger me-2"></i>
                    Quotation Preview - {formData?.ref_no}

                </Offcanvas.Title>

                {/* Edit and Download buttons at top right with spacing */}
                <div className="position-absolute top-0 end-0 p-3 d-flex gap-2 me-4">
                    {/* Edit button - only show for system-generated quotations */}
                    {!isUploaded && onEdit && (
                        <Button
                            variant="light"
                            onClick={onEdit}
                            size="sm"
                            className="shadow-sm"
                            title="Edit Quotation"
                        >
                            <FaEdit className="me-1" />
                            Edit
                        </Button>
                    )}

                    {/* Download button - different behavior for uploaded vs system */}
                    <Button
                        variant="light"
                        onClick={isUploaded ? handleDocumentDownload : onDownload}
                        size="sm"
                        className="shadow-sm"
                        title={isUploaded ? "Download Document" : "Download PDF"}
                    >
                        <FaDownload className="me-1" />
                        {isUploaded ? 'Download' : 'PDF'}
                    </Button>



                </div>
            </Offcanvas.Header>

            <Offcanvas.Body style={{
                padding: '0',
                backgroundColor: '#f8f9fa',
                overflowY: 'auto',
                overflowX: 'hidden'
            }}>
                {isUploaded ? renderUploadedDocumentPreview() : renderSystemGeneratedPreview()}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default QuotationPreviewModal;
